type EquipmentSizeLike = {
  id: number;
  name?: string | null;
};

type EquipmentWithStatus = {
  id: number;
  size?: { id: number; name?: string | null } | null;
  status?: { id: number; name?: string | null } | null;
};

/**
 * Filters equipment sizes to remove duplicates by both ID and name.
 * Keeps the first occurrence of each unique size (by ID or by normalized name).
 *
 * @param items - Array of equipment sizes to filter
 * @returns Filtered array with duplicates removed
 */
export const filterEquipmentSizes = <T extends EquipmentSizeLike>(items: T[]): T[] => {
  const seenIds = new Set<number>();
  const seenNames = new Set<string>();

  return items.filter((item) => {
    // Filter out items without valid ID
    if (!item.id) {
      return false;
    }

    // Normalize the name for comparison (trim, lowercase, remove extra whitespace)
    const normalizedName = item.name?.trim().toLowerCase().replace(/\s+/g, ' ') || '';

    // Filter out items without a name
    if (!normalizedName) {
      return false;
    }

    // Check for duplicate ID
    if (seenIds.has(item.id)) {
      return false;
    }

    // Check for duplicate normalized name
    if (seenNames.has(normalizedName)) {
      return false;
    }

    // This is a unique item - add to seen sets
    seenIds.add(item.id);
    seenNames.add(normalizedName);
    return true;
  });
};

/**
 * Filters equipment sizes to only include sizes from active equipment.
 * A size is considered available if it exists on at least one active equipment.
 * Equipment is considered active if its status name is 'InService' (case-insensitive).
 *
 * @param sizes - Array of equipment sizes to filter
 * @param equipment - Array of equipment with status information
 * @returns Filtered array containing only sizes from active equipment
 */
export const filterEquipmentSizesByActiveEquipment = <T extends EquipmentSizeLike>(sizes: T[], equipment: EquipmentWithStatus[]): T[] => {
  // Create a set of size IDs that exist on active equipment
  const activeSizeIds = new Set<number>();

  equipment.forEach((eq) => {
    const statusName = eq.status?.name?.toLowerCase().trim();
    // Consider equipment active if status is 'InService' (case-insensitive)
    const isActive = statusName === 'inservice' || statusName === 'in service';

    if (isActive && eq.size?.id) {
      activeSizeIds.add(eq.size.id);
    }
  });

  // Filter sizes to only include those that exist on active equipment
  return sizes.filter((size) => activeSizeIds.has(size.id));
};
